import { useLocation, useNavigate } from "react-router-dom";
import { GeneralCommonHeader } from "../common/GeneralCommonHeader";
import {
  DISPLAY_HEADER_ICON_BACK,
  NUMBER_OF_ITEMS_DISPLAYED_PER_PAGE,
  DISPLAY_PAGE_SEARCH_HISTORY,
} from "../../constants/common_constants";
import { useEffect, useRef, useState } from "react";
import {
  SortOrder,
  FilterItem,
  useDtsAppContext,
} from "../provider/DtsAppContextProvider";
import { calcUtcDatetime } from "../../utils/basis_util";
import { SearchHistoryList } from "../../features/components/search_history/ui_elements/SearchHistoryList";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { Pager } from "../common/Pager";
import { useApi, useErrorDialog, useInitUsageLog } from "../../hooks";
import { ISearchHistoryListType } from "../../features/components/search_history/types/search_history_type";
import { SearchHistoryAccordionMenu } from "../../features/components/search_history/ui_elements/SearchHistoryAccordionMenu";
import {
  deleteSearchHistory,
  getSearchHistoryList,
} from "../../features/api/dts_api_wrapper";

export const SearchHistory = () => {
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();
  const { handleApi } = useApi();
  //検索履歴一覧のrefを取得
  const listElement = useRef<HTMLDivElement>(null);

  // 他のページから遷移された時の処理
  const location = useLocation();
  //未回答数ボタンから遷移してきたかどうかを格納
  const fromUnansweredButton: boolean =
    location.state?.isFromUnanswered ?? false;

  // ユーザー情報等
  const {
    dacSearchHistorySortOrder,
    setDacSearchHistorySortOrder,
    dacSearchHistoryPageNum,
    setDacSearchHistoryPageNum,
    dacFilterItem,
    setDacFilterItem,
    dacFilterCriteria,
    setDacFilterCriteria,
  } = useDtsAppContext();

  const userId =
    localStorage.getItem(
      `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
    ) || "";
  const displayPage = DISPLAY_PAGE_SEARCH_HISTORY;
  const displayDateTime = calcUtcDatetime(); //現在日時(UTC時刻)

  // 表示設定メニューが開いているかどうかを格納
  const [open, setOpen] = useState<boolean>(false);

  // 並び順項目設定用(文字列リテラル)
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // 1ページ当たりに表示される件数
  const pagePerNum = NUMBER_OF_ITEMS_DISPLAYED_PER_PAGE;

  //取得した検索履歴一覧, 全ページ数, ページ番号を格納
  const [searchHistoryList, setSearchHistoryList] =
    useState<ISearchHistoryListType>({
      searchHistoryInfo: [],
      totalPageNum: 1,
      pageNum: 1,
    });

  //検索履歴情報IDを格納(削除用)
  const [searchHistoryId, setSearchHistoryId] = useState<string>("");

  //フィルター項目とフィルター条件の初期値を格納
  const defaultFilterItem: FilterItem = fromUnansweredButton ? "status" : "";
  const defaultFilterCriteria: string = fromUnansweredButton ? "0" : "";
  //フィルター項目設定用
  const [filterItem, setFilterItem] = useState<FilterItem>(defaultFilterItem);
  //フィルター条件設定用
  const [filterCriteria, setFilterCriteria] = useState<string>(
    defaultFilterCriteria,
  );

  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  //確認ダイアログ関連
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isShowConfirmDialog, setIsShowConfirmDialog] = useState(false);

  const showConfirmDialog = (message: string) => {
    setConfirmMessage(message);
    setIsShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setConfirmMessage("");
    setIsShowConfirmDialog(false);
  };

  // 初期表示時の利用ログ登録
  const { handleInit: initUsageLog } = useInitUsageLog({
    userId,
    displayPage,
    displayDateTime,
  });

  //確認ダイアログCancelボタン押下時の処理
  const handleOnCancel = () => {
    closeConfirmDialog();
  };
  //確認ダイアログOKボタン押下時の処理
  const handleOnOk = async () => {
    closeConfirmDialog();
    //LocalstorageまたはContextから並び順, フィルター項目, フィルター条件を取得/格納
    const _sortOrder = fromUnansweredButton
      ? "desc"
      : (((localStorage.getItem("searchHistorySortOrder") ||
          dacSearchHistorySortOrder) ??
          sortOrder) as SortOrder);
    const _filterItem = fromUnansweredButton
      ? "status"
      : (((localStorage.getItem("filterItem") || dacFilterItem) ??
          filterItem) as FilterItem);
    const _filterCriteria = fromUnansweredButton
      ? "0"
      : ((localStorage.getItem("filterCriteria") || dacFilterCriteria) ??
        filterCriteria);
    if (
      !userId ||
      !searchHistoryId ||
      !searchHistoryList.pageNum ||
      !pagePerNum ||
      !_sortOrder
    ) {
      //パラメータ不足での取得, 削除失敗のとき, トップ画面に遷移する
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      return; // パラメータ不足の場合は処理を終了
    }
    try {
      //検索履歴情報削除
      const deleteSearchHistoryResult = await handleApi(
        deleteSearchHistory,
        userId,
        searchHistoryId,
      );
      if ("errorMessage" in deleteSearchHistoryResult) {
        console.log("errorMessage: ", deleteSearchHistoryResult.errorMessage);
        throw new Error("failed to delete searchHistory Info");
      }

      //検索履歴情報一覧取得
      await getSearchHistoryListInfo(
        searchHistoryList.pageNum,
        _sortOrder,
        _filterItem,
        _filterCriteria,
      );
    } catch (err) {
      console.log(`[${displayPage}]handleOnOk err :`, err);
      //パラメータ不足以外での検索履歴情報の削除, 検索履歴一覧の取得に失敗のとき, その場に留まる
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
      ); //エラー", "通信エラーが発生しました。
    }
  };

  //適用(Apply)ボタン押下時の処理
  const handleOnApply = async () => {
    //LocalstorageまたはContextから並び順, フィルター項目, フィルター条件を取得/格納
    const _sortOrder = (localStorage.getItem("searchHistorySortOrder") ??
      dacSearchHistorySortOrder) as SortOrder;
    const _filterItem = (localStorage.getItem("filterItem") ??
      dacFilterItem) as FilterItem;
    const _filterCriteria =
      localStorage.getItem("filterCriteria") ?? dacFilterCriteria;

    if (!userId || !searchHistoryList.pageNum || !pagePerNum || !sortOrder) {
      //パラメータ不足での取得失敗のとき, トップ画面に遷移する
      console.log(
        `[${displayPage}] handleOnApply err: failed to get search history`,
      );
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      //表示設定の表示を変更前に戻す処理
      setSortOrder(_sortOrder);
      setFilterItem(_filterItem);
      setFilterCriteria(_filterCriteria);

      return; // パラメータ不足の場合は処理を終了
    }
    try {
      //検索履歴一覧の取得
      await getSearchHistoryListInfo(
        searchHistoryList.pageNum,
        sortOrder,
        filterItem,
        filterCriteria,
      );
      //正常終了時、アコーディオンメニューを閉じる
      setOpen(false);
    } catch (err) {
      //表示設定の表示を変更前に戻す処理
      setSortOrder(_sortOrder);
      setFilterItem(_filterItem);
      setFilterCriteria(_filterCriteria);
      console.log(`[${displayPage}]handleOnApply err :`, err);
      //パラメータ不足以外での検索履歴一覧の取得に失敗のとき, その場に留まる
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
      ); //エラー", "通信エラーが発生しました。
    }
  };

  // ページ切り替え時
  const handlePageChange = async (page: number) => {
    //LocalstorageまたはContextから並び順, フィルター項目, フィルター条件を取得/格納
    const _sortOrder = fromUnansweredButton
      ? "desc"
      : (((localStorage.getItem("searchHistorySortOrder") ||
          dacSearchHistorySortOrder) ??
          sortOrder) as SortOrder);
    const _filterItem = fromUnansweredButton
      ? "status"
      : (((localStorage.getItem("filterItem") || dacFilterItem) ??
          filterItem) as FilterItem);
    const _filterCriteria = fromUnansweredButton
      ? "0"
      : ((localStorage.getItem("filterCriteria") || dacFilterCriteria) ??
        filterCriteria);
    // 必要なパラメータが不足している場合、エラーダイアログを表示してトップページに遷移
    if (!userId || !page || !pagePerNum || !_sortOrder) {
      console.log(
        `[${displayPage}] handlePageChange err: failed to get search history`,
      );
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      );
      return; // パラメータ不足の場合は処理を終了
    }
    if (open) {
      //表示設定メニューが開いていたら閉じる
      setOpen(false);
    }
    returnTop();
    try {
      // 検索履歴一覧を取得
      await getSearchHistoryListInfo(
        page,
        _sortOrder,
        _filterItem,
        _filterCriteria,
      );
    } catch (err) {
      console.log(`[${displayPage}] handlePageChange error:`, err);
      // パラメータ不足以外のエラー時にはその場に留まる
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
      );
    }
  };

  //Context or ローカルストレージからデータ取得, 検索履歴一覧取得
  const getInitialInfo = async () => {
    try {
      //利用ログ登録
      const isError = await initUsageLog();
      if (isError) {
        return;
      }
      //並び順、ページ番号、フィルター項目、フィルター条件をContext or ローカルストレージから読み出す
      const _sortOrder = fromUnansweredButton
        ? "desc"
        : localStorage.getItem("searchHistorySortOrder") ||
          dacSearchHistorySortOrder ||
          "";
      const _pageNum =
        Number(localStorage.getItem("searchHistoryPageNum")) ||
        dacSearchHistoryPageNum ||
        NaN;
      const _filterItem = fromUnansweredButton
        ? "status"
        : ((localStorage.getItem("filterItem") || dacFilterItem) ?? null);
      const _filterCriteria = fromUnansweredButton
        ? "0"
        : ((localStorage.getItem("filterCriteria") || dacFilterCriteria) ??
          null);

      if (
        _sortOrder &&
        _pageNum &&
        _filterItem !== null &&
        _filterCriteria !== null
      ) {
        //読み出し成功した場合, stateに保存
        setSortOrder(_sortOrder as SortOrder);
        setSearchHistoryList((prevState) => ({
          ...prevState,
          pageNum: _pageNum,
        }));
        setFilterItem(_filterItem as FilterItem);
        setFilterCriteria(_filterCriteria);
        if (
          dacSearchHistorySortOrder !== (_sortOrder as SortOrder) ||
          !dacSearchHistoryPageNum ||
          dacFilterItem !== (_filterItem as FilterItem) ||
          dacFilterCriteria !== _filterCriteria
        ) {
          //ページ更新等でContextが初期化された(localStorageと値が異なる)場合, ContextにLocalstorageの値をセット
          setDacSearchHistorySortOrder(_sortOrder as SortOrder);
          setDacSearchHistoryPageNum(_pageNum);
          setDacFilterItem(_filterItem as FilterItem);
          setDacFilterCriteria(_filterCriteria);
        }
        //検索履歴一覧の取得(初回)
        await getSearchHistoryListInfo(
          _pageNum,
          _sortOrder as SortOrder,
          _filterItem as FilterItem,
          _filterCriteria,
        );
      } else {
        //Context、LocalStorageのどちらからも取得できなかった場合, state、Context、LocalStorageに初期値を格納
        setSortOrder("desc");
        setSearchHistoryList((prevState) => ({ ...prevState, pageNum: 1 }));
        setDacContextAndLocalStorage(
          1,
          "desc",
          defaultFilterItem,
          defaultFilterCriteria,
        );
        //初期値での検索履歴一覧の取得
        await getSearchHistoryListInfo(
          1,
          "desc",
          defaultFilterItem,
          defaultFilterCriteria,
        );
      }
    } catch (err) {
      console.log(`[${displayPage}]getInitialInfo err: `, err);
      showErrorDialog(
        t("共通エラーポップ.エラー"),
        t("共通エラーポップ.通信エラーが発生しました。"),
        "navigateToTopPage",
      ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
    }
  };

  //検索履歴一覧の取得処理(呼び出し元でのエラー処理必須)
  const getSearchHistoryListInfo = async (
    pageNum: number,
    sortOrder: SortOrder,
    filterItem: FilterItem,
    filterCriteria: string,
  ) => {
    //検索履歴一覧の取得
    const searchHistoryListResult = await handleApi(
      getSearchHistoryList,
      userId,
      pageNum,
      pagePerNum,
      sortOrder,
      filterItem,
      filterCriteria,
    );
    console.log("searchHistoryListResult", searchHistoryListResult);
    //検索履歴一覧の取得でエラーが出た場合, throw(呼び出し元に判断させる)
    if ("errorMessage" in searchHistoryListResult) {
      console.log("errorMessage: ", searchHistoryListResult.errorMessage);
      throw new Error("failed to get search history list");
    }
    //取得した検索履歴一覧をStateに保存
    const searchHistoryListStr = JSON.stringify(searchHistoryListResult);
    const searchHistoryListJson: ISearchHistoryListType =
      JSON.parse(searchHistoryListStr);
    setSearchHistoryList(searchHistoryListJson);
    setSortOrder(sortOrder);
    setFilterItem(filterItem);
    setFilterCriteria(filterCriteria);
    //並び順, ページ番号, フィルター項目, フィルター条件をContext + localStorageに保存
    setDacContextAndLocalStorage(
      searchHistoryListJson.pageNum,
      sortOrder,
      filterItem,
      filterCriteria,
    );
  };

  //並び順, ページ番号, フィルター項目, フィルター条件をContext + localStorageに保存する関数
  const setDacContextAndLocalStorage = (
    pageNum: number,
    sortOrder: SortOrder,
    filterItem: FilterItem,
    filterCriteria: string,
  ) => {
    //並び順
    setDacSearchHistorySortOrder(sortOrder);
    localStorage.setItem("searchHistorySortOrder", sortOrder);
    //ページ番号
    setDacSearchHistoryPageNum(pageNum);
    localStorage.setItem("searchHistoryPageNum", String(pageNum));
    //フィルター項目
    setDacFilterItem(filterItem);
    localStorage.setItem("filterItem", filterItem);
    //フィルター条件
    setDacFilterCriteria(filterCriteria);
    localStorage.setItem("filterCriteria", filterCriteria);
  };

  //初回表示時の処理
  useEffect(() => {
    // 初期状態としてコンテキストorローカルストレージからデータを取得or保存
    getInitialInfo();
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key ===
        `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`
      ) {
        const authUser = localStorage.getItem(
          `CognitoIdentityServiceProvider.${process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID}.LastAuthUser`,
        );
        if (!authUser) {
          console.log(`[${displayPage}]err: userId is unknown`);
          showErrorDialog(
            t("共通エラーポップ.エラー"),
            t("共通エラーポップ.通信エラーが発生しました。"),
            "navigateToTopPage",
          ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //検索履歴一覧のスクロールバーをトップに移動させる関数
  const returnTop = () => {
    listElement.current?.scrollTo({ top: 0 });
  };

  //検索履歴一覧をレンダリングする関数
  const renderSearchHistoryList = () => {
    return searchHistoryList.searchHistoryInfo.map(
      (searchHistoryInfo, index) => (
        <SearchHistoryList
          searchHistoryInfo={searchHistoryInfo}
          userId={userId}
          showConfirmDialog={showConfirmDialog}
          setSearchHistoryId={setSearchHistoryId}
          isLastList={searchHistoryList.searchHistoryInfo.length === index + 1}
          key={searchHistoryInfo.searchHistoryId}
        />
      ),
    );
  };

  return (
    // スクロール不可に設定
    <div className="flex flex-col h-svh overflow-hidden">
      {/* header */}
      <GeneralCommonHeader
        title={t("検索履歴画面.検索履歴")} //検索履歴
        displayheaderIcon={DISPLAY_HEADER_ICON_BACK}
        onBtnClick={() => {
          navigate(-1);
        }}
      />
      {/* header以下 */}
      <div className="flex flex-col mx-4 mt-4 h-full overflow-hidden">
        {!fromUnansweredButton && (
          <SearchHistoryAccordionMenu
            open={open}
            sortOrder={sortOrder}
            filterItem={filterItem}
            filterCriteria={filterCriteria}
            setOpen={setOpen}
            setSortOrder={setSortOrder}
            setFilterItem={setFilterItem}
            setFilterCriteria={setFilterCriteria}
            handleOnApply={handleOnApply}
          />
        )}
        <div
          className="h-full overflow-y-scroll overflow-x-hidden"
          ref={listElement}
        >
          {searchHistoryList.searchHistoryInfo.length ? (
            renderSearchHistoryList()
          ) : (
            <p className="h-full flex items-center justify-center">
              {t("検索履歴画面.検索履歴がありません。")}
            </p>
          )}
        </div>
      </div>
      <Pager
        currentPage={searchHistoryList.pageNum}
        totalPages={searchHistoryList.totalPageNum}
        onPageChange={handlePageChange}
      />
      {isShowConfirmDialog && (
        <ConfirmDialog
          message={confirmMessage}
          onCancel={handleOnCancel}
          onOk={handleOnOk}
        ></ConfirmDialog>
      )}
    </div>
  );
};
